/**
 * /checkin/admin/conversations/:id — Conversation Overview.
 * Default view: summary + themes + insights + journey. Transcript
 * requires explicit confirmation (Super Admin only).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CHECKIN_PALETTE as P } from "../_shell";
import { getToken } from "@/lib/checkinApi";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const BASE = `${BACKEND}/api/kcis/admin/intelligence`;

async function api(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method || "GET",
    headers: { "Content-Type": "application/json",
                Authorization: `Bearer ${getToken()}` },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) throw new Error((await res.json()).detail?.code || `HTTP ${res.status}`);
  return res.json();
}

export default function ConversationOverview() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [journey, setJourney] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const c = await api(`/conversations/${encodeURIComponent(id)}`);
      setData(c);
      const j = await api(`/users/${c.user.id}/journey`);
      setJourney(j.events || []);
    } catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openTranscript = async () => {
    if (reason.trim().length < 8) {
      setError("Please enter a purpose (≥ 8 characters) for the audit log."); return;
    }
    setBusy(true); setError("");
    try {
      const t = await api(`/conversations/${encodeURIComponent(id)}/transcript`,
        { method: "POST", body: { reason } });
      setTranscript(t.messages || []);
      setConfirming(false);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const doExport = async (kind) => {
    const purpose = window.prompt(`Purpose for ${kind} export?`) || "";
    if (purpose.length < 4) return;
    try {
      const r = await api("/export",
        { method: "POST", body: { conversation_id: id, kind, purpose } });
      const blob = new Blob([JSON.stringify(r, null, 2)],
                              { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `${id.slice(0,8)}-${kind}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  };

  if (!data) return <div className="p-6 text-sm" style={{ background: P.warmBg, color: "#5F5766", minHeight:"100vh" }}>{error || "Loading…"}</div>;
  const c = data.conversation, u = data.user, s = data.summary || {}, sc = data.relationship_score || {};

  return (
    <div style={{ background: P.warmBg, color: P.ink, minHeight: "100vh" }}
         data-testid="intel-overview-page">
      <div className="border-b" style={{ background:"#FFFFFF", borderColor: P.silverSoft }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: P.purple }}>
              Conversation Intelligence
            </div>
            <div className="text-xl font-semibold mt-1" data-testid="intel-overview-title">
              {s.title || "Your Check In"}
            </div>
            <div className="text-xs" style={{ color: "#5F5766" }}>
              {u.display || "—"} · {u.email || "—"} · {u.phone_masked || "—"} · {c.state} · {c.access_type}
            </div>
          </div>
          <Link to="/checkin/admin/conversations" className="text-xs underline" style={{ color: P.purple }}>
            ← Back
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {/* Summary */}
        <Card title="AI Summary" testid="intel-card-summary">
          <Field label="Current concern" text={s.current_concern} />
          <Field label="Themes" list={s.primary_themes} />
          <Field label="Emotional observations" text={s.emotional_observations} />
          <Field label="Key insight" text={s.key_insight} accent={P.purple} />
          <Field label="Suggested next step" text={s.suggested_next_step} />
          <Field label="Reflection question" text={s.reflection_question} />
          <Field label="Suggested next check-in" text={s.suggested_next_checkin} accent={P.green} />
        </Card>

        {/* Progress */}
        <Card title="Progress (internal)" testid="intel-card-progress">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Metric label="Trust score" value={sc.trust_score ?? 0} />
            <Metric label="Total conversations" value={sc.conversations_total ?? 0} />
            <Metric label="Days active" value={sc.days_active ?? 0} />
            <Metric label="Avg return days" value={sc.avg_return_days ?? 0} />
          </div>
        </Card>

        {/* Insights */}
        <Card title="AI Insights" testid="intel-card-insights">
          {(data.insights || []).length === 0 && (
            <div className="text-sm" style={{ color: "#5F5766" }}>
              No operational insights yet — more conversations needed.
            </div>
          )}
          {(data.insights || []).map((i, ix) => (
            <div key={ix} className="text-sm py-1"
                 data-testid={`intel-insight-${i.kind}`}>
              <span className="font-medium">{i.title}</span>
              {i.detail && <span style={{ color: "#5F5766" }}> — {i.detail}</span>}
            </div>
          ))}
        </Card>

        {/* Journey */}
        <Card title="User Journey" testid="intel-card-journey">
          {(journey || []).map((ev, ix) => (
            <div key={ix} className="text-sm py-1"
                 data-testid={`intel-journey-${ev.kind}-${ix}`}>
              <span className="text-[11px]" style={{ color: "#9E97AB" }}>
                {(ev.at || "").slice(0, 10)}
              </span>
              {" · "}<b>{ev.label}</b>
            </div>
          ))}
        </Card>

        {/* Transcript access */}
        <Card title="Full Conversation" testid="intel-card-transcript">
          {!transcript && !confirming && (
            <button onClick={() => setConfirming(true)}
                    data-testid="intel-view-transcript-btn"
                    className="px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: P.purple }}>View Full Conversation</button>
          )}
          {confirming && !transcript && (
            <div className="rounded-xl p-4 text-sm"
                 style={{ background: "#FDF6E4", border: "1px solid #F1E3B0", color: "#8B6B14" }}
                 data-testid="intel-transcript-confirm">
              <div>This conversation may contain highly personal information.
                Access is restricted to the Super Administrator. Every access is permanently recorded.</div>
              <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                         placeholder="Purpose for this access (≥ 8 characters)"
                         data-testid="intel-transcript-reason"
                         className="w-full mt-3 px-2 py-1 rounded-md"
                         style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
              <div className="mt-3 flex gap-2">
                <button onClick={openTranscript} disabled={busy}
                        data-testid="intel-transcript-continue"
                        className="px-3 py-2 rounded-lg text-white text-sm disabled:opacity-50"
                        style={{ background: P.purple }}>
                  {busy ? "Loading…" : "Continue"}
                </button>
                <button onClick={() => { setConfirming(false); setReason(""); }}
                        data-testid="intel-transcript-cancel"
                        className="px-3 py-2 rounded-lg text-sm"
                        style={{ background: P.silverSoft, color: P.ink }}>Cancel</button>
              </div>
            </div>
          )}
          {transcript && (
            <div className="space-y-2 mt-2" data-testid="intel-transcript-body">
              {transcript.map((m, ix) => (
                <div key={m.id || ix} className="text-sm"
                     data-testid={`intel-msg-${ix}`}>
                  <span className="text-[11px] uppercase" style={{ color: "#9E97AB" }}>
                    {m.role === "user" ? "USER" : "DR C"} · {(m.created_at || "").slice(11, 19)}
                  </span>
                  <div style={{ color: P.ink }}>{m.content}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Exports */}
        <Card title="Export" testid="intel-card-export">
          <div className="flex gap-2 flex-wrap">
            {["summary", "journey", "insights", "audit", "transcript"].map((k) => (
              <button key={k} onClick={() => doExport(k)}
                      data-testid={`intel-export-${k}`}
                      className="px-3 py-1.5 rounded-lg text-sm"
                      style={{ background: P.silverSoft, color: P.ink }}>
                Export {k}
              </button>
            ))}
          </div>
          <div className="text-[11px] mt-2" style={{ color: "#9E97AB" }}>
            Every export is watermarked "Confidential" and recorded in the audit trail.
          </div>
        </Card>

        {error && (
          <div className="text-sm p-3 rounded-lg"
               style={{ background: "#FEEEF1", color: P.danger }}
               data-testid="intel-error">{error}</div>
        )}
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-2xl p-5"
         style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}
         data-testid={testid}>
      <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
function Field({ label, text, list, accent }) {
  if (!text && (!list || list.length === 0)) return null;
  return (
    <div className="mt-2">
      <div className="text-[11px] uppercase" style={{ color: "#9E97AB" }}>{label}</div>
      {text && <div className="text-sm mt-1" style={{ color: accent || P.ink,
                                                          fontWeight: accent ? 600 : 400 }}>{text}</div>}
      {list && list.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {list.map((t) => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full"
                   style={{ background: "#F5F1FB", color: "#5F5766" }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}
function Metric({ label, value }) {
  return (
    <div className="rounded-xl p-3"
         style={{ background: "#FBF9FE", border: `1px solid #EFE9F8` }}>
      <div className="text-[10px] uppercase" style={{ color: "#9E97AB" }}>{label}</div>
      <div className="text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}
